import { Button, Grid, Box, IconButton, Stack, Typography, TextField as MuiTextField, Checkbox } from '@mui/material';
import { useForm } from 'react-hook-form';
import CloseIcon from '@mui/icons-material/Close';
import { FormProvider, SelectTag, Table, TextField } from '../../../../../components';
import { useCallback, useEffect, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { SelectDoctorTable } from '../../HMS/masters/others/SelectDoctorTable';
import { dateFormatter, formatDate, request, toast } from '../../../../../utils';

const STOCK_STATUS_HEADER = [
  {
    header: 'CNT No.',
    accessor: 'cnt'
  },
  {
    header: 'CNT Type',
    accessor: 'type'
  },
  {
    header: 'CNT Date',
    accessor: (row) => formatDate(row?.cnt_date)
  },
  {
    header: 'Invoice No',
    accessor: 'invoice_no'
  },
  {
    header: 'Invoice Date',
    accessor: (row) => formatDate(row?.invoice_date)
  },
  {
    header: 'Product Type',
    accessor: 'product_type'
  },
  {
    header: 'Location',
    accessor: (row) => row?.location?.warehouse_name
  }
];

export const GrnConsignmentAddDetails = ({ onClose, onAdd, supplier }) => {
  const [selectedRows, setSelectedRows] = useState([]);
  const [consignmentData, setConsignmentData] = useState([]);
  const [allAddDetails, setAllAddDetails] = useState([]);
 

  const methods = useForm({
    resolver: yupResolver(
      Yup.object().shape({})
    ),
    defaultValues: {},
    mode: 'all'
  });
  const { reset } = methods;

  const { handleSubmit, setValue, watch } = methods;

  const formSubmitHandler = (formValues) => {
    onAdd(allAddDetails);       
    reset();
    onClose()
  };
  

  const isSelected = (row, field) => {
    if (!Array.isArray(selectedRows)) {
      console.error('selectedRows is not an array:', selectedRows);
      return false;
    }
    const selectedRow = selectedRows.find((selectedRow) => selectedRow._id === row._id);
    return selectedRow ? selectedRow[field] : false;
  };

  useEffect(() => {
    if (Array.isArray(selectedRows)) {
      const details = selectedRows.flatMap(row => {
        const cnt = row.cnt;
        return (row.add_details || []).map(detail => ({
          ...detail,
        consignment_no:cnt, 
        }));
      });
      setAllAddDetails(details);
    }
  }, [selectedRows]);
  

  const handleRowSelect = (row, field) => {
    if (!row || !field) {
      console.warn('Row data or field is undefined. Check the input.');
      return;
    }
    if (!Array.isArray(selectedRows)) {
      console.error('selectedRows is not an array:', selectedRows);
      setSelectedRows([]);
      return;
    }
    setSelectedRows((prevSelectedRows) => {
      const existingRow = prevSelectedRows.find((selectedRow) => selectedRow._id === row._id);
      if (existingRow) {
        if (existingRow[field]) {
          return prevSelectedRows.filter((selectedRow) => selectedRow._id !== row._id);
        } else {
          return prevSelectedRows.map((selectedRow) =>
            selectedRow._id === row._id ? { ...selectedRow, [field]: true } : selectedRow
          );
        }
      } else {
        return [...prevSelectedRows, { ...row, [field]: true }];
      }
    });
  };

  const watchCntNo = watch('cnt_no');
  const watchCntDate = watch('cnt_date');
  const watchInvNo = watch('invoice_no');
  
  const fetchBySupplier = async () => {
    setConsignmentData({ loading: true });
    const response = await request('/consignment-receipt-supplier-list', {
      method: 'GET',
      query: { supplier },
    });
  
    if (response.success) {
      const sortedData = response.data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      const filteredData = sortedData.filter(
        (item) =>
          item.status === 'active' ||
          item.status === 'inactive' ||
          item.status === 'default'
      );
      setConsignmentData({ loading: false, data: filteredData });
    } else {
      toast('Error in fetching data', { variant: 'error' });
      setConsignmentData({ loading: false });
    }
  };
  

  const handleFetchConsignments = async () => {
    setConsignmentData({ loading: true });
  
    const query = {
      supplier,
      cnt_no: watchCntNo,
      cnt_date: watchCntDate,
      invoice_no: watchInvNo,
    };
  
    const response = await request('/consignment-receipt-supplier-list', {
      method: 'GET',
      query,
    });
  
    if (response.success) {
      const sortedData = response.data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      const filteredData = sortedData.filter(
        (item) =>
          item.status === 'active' ||
          item.status === 'inactive' ||
          item.status === 'default'
      );
      setConsignmentData({ loading: false, data: filteredData });
    } else {
      toast('Error in fetching data', { variant: 'error' });
      setConsignmentData({ loading: false });
    }
  };
  
  useEffect(() => {
    fetchBySupplier();
  }, [supplier]);
  
  const currentAction = 'new';
  


  return (
    <Stack spacing={1} sx={{ height: '100%' }}>
      <Stack
        padding={1.5}
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
        borderBottom="1px solid"
        borderColor="grey.300"
      >
        <Typography variant="h4" fontWeight="bold">
          Convert Consignment To GRN
        </Typography>
        <IconButton onClick={onClose}>
          <CloseIcon color="error" fontSize="large" />
        </IconButton>
      </Stack>
      <FormProvider
        sx={{ height: '100%', overflow: 'auto' }}
        methods={methods}
        onSubmit={handleSubmit(formSubmitHandler)}
      >
        <Grid container spacing={1} sx={{ flex: 1, height: '100%', overflow: 'auto' }}>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3, px: 2 }}>
              <Stack spacing={1} direction="row" alignItems="center">
                <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: '1.4rem' }}>CNT No</Typography>
                <TextField sx={{ flex: 0.4 }} name="cnt_no" />
                <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: '1.4rem' }}>CNT Date</Typography>
                <TextField sx={{ flex: 0.4 }} type="date" name="cnt_date" />
                <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: '1.4rem' }}>Invoice No.</Typography>
                <TextField sx={{ flex: 0.4 }} name="invoice_no" />
              </Stack>
              <Stack  direction="row" alignItems="center" justifyContent="flex-end">
              <Button variant="contained" onClick={handleFetchConsignments}>
                Search
              </Button>
          </Stack>
            </Box>
            <Stack sx={{ mb: 0.3, mt: 5 }} direction="row" spacing={2} minHeight={'30rem'}>
              <SelectDoctorTable
                containerSX={{ flex: 1, border: '1px solid rgba(0,0,0,0.27)' }}
                data={consignmentData?.data || []}
                columns={STOCK_STATUS_HEADER}
                size="small"
                showInputField={true}
                showOk={true}
                isSelected={isSelected}
                handleRowSelect={handleRowSelect}
                selectedRows={selectedRows}
                currentAction={currentAction}
              />
            </Stack>
            <Stack spacing={1} direction="row" alignItems="center" justifyContent="flex-end">
              <Button variant="contained" type="submit">
                Add
              </Button>
              <Button variant="outlined" onClick={onClose} color="error">
                Close
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </FormProvider>
    </Stack>
  );
};
